import { createContext, useContext, useState, useCallback, useEffect } from "react";

export type ConsentTier = "analytics" | "marketing";

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  hasResponded: boolean;
}

interface ConsentContextValue {
  consent: ConsentState;
  hasConsent: (tier: ConsentTier) => boolean;
  setConsent: (tier: ConsentTier, value: boolean) => void;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  markResponded: () => void;
}

const STORAGE_KEYS = {
  analytics: "consent_analytics",
  marketing: "consent_marketing",
  responded: "consent_responded",
} as const;

function readStorage(): ConsentState {
  try {
    return {
      analytics: localStorage.getItem(STORAGE_KEYS.analytics) === "true",
      marketing: localStorage.getItem(STORAGE_KEYS.marketing) === "true",
      hasResponded: localStorage.getItem(STORAGE_KEYS.responded) === "true",
    };
  } catch {
    return { analytics: false, marketing: false, hasResponded: false };
  }
}

function writeStorage(tier: ConsentTier | "responded", value: boolean) {
  try {
    const key = tier === "responded" ? STORAGE_KEYS.responded : STORAGE_KEYS[tier];
    localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsentState] = useState<ConsentState>(readStorage);

  const setConsent = useCallback((tier: ConsentTier, value: boolean) => {
    writeStorage(tier, value);
    writeStorage("responded", true);
    setConsentState(prev => ({ ...prev, [tier]: value, hasResponded: true }));
    window.dispatchEvent(new CustomEvent("consentChanged"));
  }, []);

  const acceptAll = useCallback(() => {
    writeStorage("analytics", true);
    writeStorage("marketing", true);
    writeStorage("responded", true);
    setConsentState({ analytics: true, marketing: true, hasResponded: true });
    window.dispatchEvent(new CustomEvent("consentChanged"));
  }, []);

  const rejectNonEssential = useCallback(() => {
    writeStorage("analytics", false);
    writeStorage("marketing", false);
    writeStorage("responded", true);
    setConsentState({ analytics: false, marketing: false, hasResponded: true });
    window.dispatchEvent(new CustomEvent("consentChanged"));
  }, []);

  const markResponded = useCallback(() => {
    writeStorage("responded", true);
    setConsentState(prev => ({ ...prev, hasResponded: true }));
  }, []);

  const hasConsent = useCallback((tier: ConsentTier) => consent[tier], [consent]);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (Object.values(STORAGE_KEYS).includes(e.key as string)) {
        setConsentState(readStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, hasConsent, setConsent, acceptAll, rejectNonEssential, markResponded }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside ConsentProvider");
  return ctx;
}
